// Ciro talebi satirlarini "enriched" hale getirir: tablonun render
// fonksiyonlarinin runtime-context'siz tuketebilecegi `targetRevenue`,
// `warningsKeys` ve `warningsOnlyUygun` field'larini ekler.
//
// Bu helper CiroTalepleriPage'in `useMemo` icindeki orijinal mantiginin saf
// JS surumudur — sayfa sislemesin diye disari alindi. CiroEklePage de ileride
// ayni enrichment'i kullanabilir.

import { evaluateEntryWarnings, REVENUE_WARNING_KEYS } from './revenueWarnings';

// Rule lookup anahtari — geriye donuk uyumluluk icin export korunuyor
// (caller'lar hala buildRuleLookupKey'i cagirabilir). Enrichment icinde
// artik kullanilmiyor cunku xyLabel direkt row.ay_sayisi / row.toplam_ay
// alanlarindan uretiliyor (compute/rule/past yok).
export const buildRuleLookupKey = (jobTypeId, revenueChannel, pricingMethodId) =>
  `${jobTypeId ?? ''}|${revenueChannel ?? ''}|${pricingMethodId ?? ''}`;

/**
 * @param {Array<Object>} entries - userRevenueEntries hook'undan gelen satirlar
 * @param {Object} options
 * @param {Map<number|string, number>} options.coefficientById
 *   - bonus_revenue_type_id → coefficient haritasi (Hedefe Esas Ciro hesabi)
 * @param {Map<number|string, string>} [options.typeNameById]
 *   - bonus_revenue_type_id → name haritasi (Ciro Turu kolonu icin)
 * @param {number|string|null} [options.currentUserTeamId]
 *   - "farkli ekip" uyarisini susturmak icin onayci ekibi (ROOT icin null)
 * @returns {Array<Object>} `targetRevenue`, `warningsKeys`, `warningsOnlyUygun`,
 *   `bonusRevenueTypeName` ile zenginlestirilmis kopya satirlar.
 */
export const enrichRevenueEntries = (entries, options = {}) => {
  const {
    coefficientById,
    typeNameById,
    currentUserTeamId = null,
    // NOT: `rulesByKey` ve `pastOverrides` parametreleri artik xyLabel
    // uretmek icin kullanilmiyor (row.ay_sayisi / row.toplam_ay direkt
    // kullaniliyor). Caller signature'i degismesin diye options acikligi
    // korundu — parametre gecebilir, ignore edilir.
  } = options;
  const list = Array.isArray(entries) ? entries : [];

  // Ayni job_id'ye ait diger entry'leri uyari (a) icin tek seferde indeksle.
  const entriesByJob = new Map();
  list.forEach((e) => {
    if (e?.job_id == null) return;
    if (!entriesByJob.has(e.job_id)) entriesByJob.set(e.job_id, []);
    entriesByJob.get(e.job_id).push(e);
  });

  // KUMULATIF SURE — satirin kendi x/y'si tek basina yaniltiyordu:
  // "3/12" gorunuyor ama ayni ise daha once 7/12 girilmisse gercekte
  // 10/12'ye gelinmis oluyor ve bu yalnizca kaydin icine girilince
  // goruluyordu. Her satir icin "o satir DAHIL o ana kadar birikmis toplam"
  // hesaplanip tabloda alt satirda gosteriliyor.
  //
  // Birikim ayni (is + ciro kanali) icin kronolojik: yil, sonra ceyrek,
  // esitse id. REJECTED kayitlar sayilmaz (geri cekilmis kabul edilir).
  const sira = (e) => (Number(e?.yil) || 0) * 100 + (Number(e?.ceyrek) || 0) * 10;
  const kumulatif = new Map(); // entry.id → toplam ay
  const gruplar = new Map();   // `${job_id}|${kanal}` → entry[]
  list.forEach((e) => {
    if (e?.job_id == null || !e?.ciro_kanali) return;
    if (e?.status === 'REJECTED') return;
    const anahtar = `${e.job_id}|${e.ciro_kanali}`;
    if (!gruplar.has(anahtar)) gruplar.set(anahtar, []);
    gruplar.get(anahtar).push(e);
  });
  gruplar.forEach((grup) => {
    grup
      .slice()
      .sort((a, b) => (sira(a) - sira(b)) || ((Number(a.id) || 0) - (Number(b.id) || 0)))
      .reduce((toplam, e) => {
        const yeni = toplam + (Number(e.ay_sayisi) || 0);
        kumulatif.set(e.id, yeni);
        return yeni;
      }, 0);
  });

  return list.map((row) => {
    const coef = coefficientById?.get?.(row?.bonus_revenue_type_id) ?? 1;
    const targetRevenue = Number(row?.tutar || 0) * coef;
    const allForJob = row?.job_id ? entriesByJob.get(row.job_id) || [] : [];
    const others = allForJob.filter((e) => e.id !== row.id);
    const warningsKeys = evaluateEntryWarnings({
      entry: row,
      job: row.job,
      // Is bazli girislerde entry.contract_id bos olabilir; bu durumda isin
      // kendi sozlesmesi (row.job.contract) fallback olarak verilir ki
      // sozlesme durumu (geldi/gelmedi) ve uzatma uyarilari
      // degerlendirilebilsin.
      contract: row.contract || row.job?.contract || null,
      firm: row.firm,
      otherEntriesForJob: others,
      currentUserTeamId,
    });
    const warningsOnlyUygun =
      warningsKeys.length === 1 && warningsKeys[0] === REVENUE_WARNING_KEYS.uygun;
    const bonusRevenueTypeName =
      typeNameById?.get?.(row?.bonus_revenue_type_id) ?? null;
    // Ekleyen kisi — tabloda kolon olarak, modalda header'da kullaniliyor.
    const personName = row?.user
      ? [row.user.first_name, row.user.last_name].filter(Boolean).join(' ').trim() || null
      : null;

    // xyLabel — sadece ONAY_SONRASI_CIROSU icin. Basit: DB'de kayitli
    // `ay_sayisi` (x) ve `toplam_ay` (y) alanlarini oldugu gibi goster.
    // Rule/compute/past hesabi YOK — kullanici bu degerleri formda kaydetti,
    // tabloda ayni goruntulenir. (Onceden karmasik hesap vardi ve kural
    // eslesmezse xy hic goruntulenmiyordu — bug'in kaynagi buydu.)
    let xyLabel = null;
    let cumulativeLabel = null;
    if (row?.ciro_kanali === 'ONAY_SONRASI_CIROSU') {
      const x = Number(row.ay_sayisi);
      const y = Number(row.toplam_ay);
      if (Number.isFinite(x) && x > 0 && Number.isFinite(y) && y > 0) {
        xyLabel = `${x}/${y}`;
        // Birikimli toplam yalnizca satirin kendi degerinden FARKLIYSA
        // gosterilir; ilk kayitta "3/12 · toplam 3/12" demek gurultu olurdu.
        const birikim = kumulatif.get(row.id);
        if (Number.isFinite(birikim) && birikim !== x) {
          cumulativeLabel = `${birikim}/${y}`;
        }
      }
    }

    // team_name: filter (contains) icin top-level flatten. row.user.team.name
    // nested oldugu icin DataTable auto-filter dot-notation icin problem yasabilir.
    const teamName = row?.user?.team?.name || null;

    return {
      ...row,
      targetRevenue,
      warningsKeys,
      warningsOnlyUygun,
      bonusRevenueTypeName,
      personName,
      xyLabel,
      cumulativeLabel,
      team_name: teamName,
    };
  });
};
